import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Receipt, CheckCircle2, TrendingUp, Calendar, ArrowRight } from "lucide-react";
import { Badge } from "../../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PortalPayments = () => {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ paid_count: 0, lifetime_total: 0, unpaid_total: 0, unpaid_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("portal_token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      try {
        const [paymentsRes, summaryRes] = await Promise.all([
          axios.get(`${API}/portal/payments?limit=100`, config),
          axios.get(`${API}/portal/payments/summary`, config),
        ]);
        setPayments(paymentsRes.data.payments || []);
        setSummary(summaryRes.data || {});
      } catch (e) {
        console.error("Failed to load payments", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (s) => s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const formatDateTime = (s) => s ? new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0046BE]/20 border-t-[#0046BE] rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="portal-payments-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-['Outfit']">Payment History</h1>
        <p className="text-sm text-slate-400 mt-1">All your successful payments across invoices.</p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-2xl p-4 sm:p-5" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}} data-testid="payments-lifetime-total">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-500/10">
              <TrendingUp className="w-[18px] h-[18px] text-emerald-600" />
            </div>
            <div>
              <p className="text-slate-400 text-xs font-medium">Lifetime Paid</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-800 mt-0.5">${(summary.lifetime_total || 0).toFixed(2)}</p>
              <p className="text-xs text-slate-400 mt-0.5">{summary.paid_count || 0} payments</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}} data-testid="payments-unpaid-total">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-500/10">
              <Receipt className="w-[18px] h-[18px] text-amber-600" />
            </div>
            <div>
              <p className="text-slate-400 text-xs font-medium">Outstanding</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-800 mt-0.5">${(summary.unpaid_total || 0).toFixed(2)}</p>
              <p className="text-xs text-slate-400 mt-0.5">{summary.unpaid_count || 0} invoices</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5 hidden lg:block" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-500/10">
              <CheckCircle2 className="w-[18px] h-[18px] text-blue-600" />
            </div>
            <div>
              <p className="text-slate-400 text-xs font-medium">Last Payment</p>
              <p className="text-sm font-semibold text-slate-800 mt-1">
                {payments.length > 0 ? formatDate(payments[0].paid_at || payments[0].created_at) : "No payments yet"}
              </p>
              {payments.length > 0 && (
                <p className="text-xs text-slate-400 mt-0.5">${(payments[0].amount || 0).toFixed(2)}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payments list */}
      <div className="bg-white rounded-2xl overflow-hidden" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
        <div className="flex items-center justify-between p-4 sm:p-5 pb-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700 font-['Outfit']">All Payments</h2>
          <span className="text-xs text-slate-400">{payments.length}</span>
        </div>

        {payments.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Receipt className="w-10 h-10 mx-auto text-slate-200 mb-3" />
            <p className="text-slate-400 text-sm">No payments yet</p>
            {summary.unpaid_count > 0 && (
              <Link to="/portal/invoices?status=unpaid" className="inline-flex items-center gap-1 mt-3 text-[#0046BE] hover:text-[#0033A0] text-sm font-medium">
                Pay outstanding invoices <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {payments.map((p) => (
              <Link
                key={p.transaction_id || p.session_id}
                to={`/portal/invoices/${p.invoice_id}`}
                className="group flex items-center gap-3 p-4 sm:p-5 hover:bg-slate-50/60 transition-colors"
                data-testid={`payment-row-${p.transaction_id}`}
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-800">Invoice #{p.invoice_number || p.invoice_id?.slice(0, 8)}</p>
                    <Badge className="text-[10px] px-2 py-0 bg-emerald-500/10 text-emerald-700 border-emerald-500/20">PAID</Badge>
                    {p.tip_amount > 0 && (
                      <Badge className="text-[10px] px-2 py-0 bg-purple-500/10 text-purple-700 border-purple-500/20">
                        +${p.tip_amount.toFixed(2)} tip
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDateTime(p.paid_at || p.created_at)}</span>
                    {p.business_name && <span className="truncate hidden sm:inline">· {p.business_name}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-base font-bold text-slate-800">${(p.amount || 0).toFixed(2)}</p>
                  <p className="text-[11px] text-slate-300 uppercase">{p.currency || "usd"}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalPayments;
